import { h } from '@logicflow/core';
import { RectResize } from '@logicflow/extension/es/NodeResize';
import { renderRotatedText } from './textUtils';
import { getThemeStyle } from '../theme';

const { view: RectResizeView, model: RectResizeModel } = RectResize;

/**
 * Participant (Pool) — BPMN 2.0 container for a process within a Collaboration.
 *
 * Visual: large rectangle with a vertical header band on the left containing
 * the participant name rotated 90°.
 */

const HEADER_WIDTH = 30;

class ParticipantView extends RectResizeView {
  getResizeShape() {
    const { model } = this.props;
    const { x, y, width, height } = model;
    const style = model.getNodeStyle();

    const left = x - width / 2;
    const top = y - height / 2;

    return h('g', {}, [
      // Main body — fill:none so edges rendered below this node in SVG
      // z-order remain visible inside the pool. The outer stroke border
      // is preserved; pointer-events:none lets children receive mouse events.
      h('rect', {
        x: left,
        y: top,
        width,
        height,
        rx: 0,
        ry: 0,
        ...style,
        fill: 'none',
        pointerEvents: 'none',
      }),
      // Header band (left side) — this IS the interactive area for the pool
      h('rect', {
        x: left,
        y: top,
        width: HEADER_WIDTH,
        height,
        fill: style.fill,
        stroke: style.stroke,
        strokeWidth: style.strokeWidth,
      }),
      // Separator line between header and body
      h('line', {
        x1: left + HEADER_WIDTH,
        y1: top,
        x2: left + HEADER_WIDTH,
        y2: top + height,
        stroke: style.stroke,
        strokeWidth: style.strokeWidth,
      }),
      // Rotated name text in header
      renderRotatedText(h, (model.properties && model.properties.name) || '', left + HEADER_WIDTH / 2, y, height - 20, { fontSize: 12, graphModel: model.graphModel }),
    ]);
  }
}

class ParticipantModel extends RectResizeModel {
  initNodeData(data) {
    super.initNodeData(data);
    this.width = data.width || data.properties?.width || 600;
    this.height = data.height || data.properties?.height || 250;
    this.radius = 0;
    this.minWidth = 200;
    this.minHeight = 100;
    this.maxWidth = 2000;
    this.maxHeight = 1500;
    // Participants should not display the default text overlay —
    // the text is rendered inside the header band by the view.
    // Reset text position to node center and clear value to prevent
    // LogicFlow from rendering a stale text overlay.
    this.text = { x: this.x, y: this.y, editable: false, draggable: false, value: '' };
    // Sync dimensions into properties so ContainmentBehavior.getBounds()
    // and BpmnWriter can read the real size even before the first resize.
    if (!data.properties?.width) this.setProperties({ width: this.width, height: this.height });
  }

  getNodeStyle() {
    const style = super.getNodeStyle();
    const t = getThemeStyle(this.graphModel, 'participant');
    style.stroke = t.stroke || '#000';
    style.strokeWidth = t.strokeWidth ?? 2;
    style.fill = t.fill || '#f5f5f5';
    return style;
  }

  getResizeOutlineStyle() {
    return { stroke: '#1890ff', strokeWidth: 1, strokeDasharray: '3,3' };
  }
}

export const Participant = {
  type: 'epx:Participant',
  view: ParticipantView,
  model: ParticipantModel,
};
